import type { OptionValue } from "@views/Upholstery/types"
import type { PdmFlatRow } from "../../pdm/normalizePdm"
import { getSeatAndRowSlices, unwrapValue } from "../../pdm/normalizePdm"

const TRIM_COMPAT_KEY = "trim"

function splitOptionLabels(label: string): string[] {
  return label
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
}

/**
 * Read PDM rows and mark each option with the trims that actually use it.
 *
 * We do not hardcode vehicle rules. We copy what PDM already says:
 * this trim + this option appear on the same row.
 *
 * Example (F-150 body type):
 * - Crew Cab Pickup appears with XLT and Tremor → those trims are stored on the option
 * - XL never appears with Crew Cab → Crew Cab is later hidden when XL is selected
 */
export function attachTrimCompatibilityToOptions(
  flat: PdmFlatRow[],
  opts: OptionValue[],
  getPdmValue: (row: PdmFlatRow) => string
): OptionValue[] {
  if (!opts.length || !flat.length) return opts

  // Build: option label → set of trims that share a PDM row with it.
  const valueToTrims = new Map<string, Set<string>>()

  for (const row of flat) {
    const trim = unwrapValue(
      row.application.sub_model ?? row.application.SubModel ?? ""
    )
    const pdmValue = getPdmValue(row)
    if (!pdmValue.trim()) continue

    // Keep the full PDM string, and also split "Manual, Power" lists.
    const labels = [pdmValue.trim(), ...splitOptionLabels(pdmValue)]
    for (const part of labels) {
      if (!part.trim()) continue
      const bucket = valueToTrims.get(part) ?? new Set<string>()
      if (trim) bucket.add(trim)
      valueToTrims.set(part, bucket)
    }
  }

  // Stamp that trim list onto each matching UI option as compatibility.
  return opts.map((opt) => {
    const label = (opt.optionValue ?? opt.optionName ?? "").trim()
    const trims = valueToTrims.get(label)
    if (!trims?.size) return opt

    const withoutOldTrim = (opt.compatibility ?? []).filter(
      (c) => c.key.toLowerCase() !== TRIM_COMPAT_KEY
    )

    return {
      ...opt,
      compatibility: [
        ...withoutOldTrim,
        { key: TRIM_COMPAT_KEY, values: Array.from(trims).sort().join(",") },
      ],
    }
  })
}

/**
 * Same as attachTrimCompatibilityToOptions, but for middle-row seat layouts.
 * Example: Z71 only offers "50-50" in PDM, so "60-40" is tagged without Z71.
 */
export function attachTrimCompatibilityToMiddleOptions(
  flat: PdmFlatRow[],
  midOpts: OptionValue[]
): OptionValue[] {
  return attachTrimCompatibilityToOptions(
    flat,
    midOpts,
    (row) => getSeatAndRowSlices(row.product).middleRow
  )
}

/**
 * Same as attachTrimCompatibilityToOptions, but for back-row seat layouts.
 * Example: a trim that only has "Bench" in PDM will not keep "60-40" tagged for it.
 */
export function attachTrimCompatibilityToBackOptions(
  flat: PdmFlatRow[],
  backOpts: OptionValue[]
): OptionValue[] {
  return attachTrimCompatibilityToOptions(
    flat,
    backOpts,
    (row) => getSeatAndRowSlices(row.product).backRow
  )
}

/**
 * True if this option is valid for the selected trim.
 * No trim rule on the option means "show it for every trim" (other cars).
 */
export function isOptionAllowedForTrim(
  option: {
    compatibility?: Array<{ key: string; values: string }> | null
  },
  selectedTrim: string
): boolean {
  const trimCompat = option.compatibility?.find(
    (c) => c.key.toLowerCase() === TRIM_COMPAT_KEY
  )
  if (!trimCompat) return true
  if (!selectedTrim.trim()) return true

  const allowed = trimCompat.values
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)

  return allowed.includes(selectedTrim.trim().toLowerCase())
}

/** Keep only options PDM lists for the selected trim. */
export function filterOptionsBySelectedTrim<
  T extends {
    compatibility?: Array<{ key: string; values: string }> | null
  }
>(options: T[], selectedTrim: string): T[] {
  if (!selectedTrim.trim()) return options
  const next = options.filter((opt) =>
    isOptionAllowedForTrim(opt, selectedTrim)
  )
  // If no option matches this trim (label mismatch, or a car with no PDM
  // trim pairing), keep the full list so the view does not go blank.
  return next.length ? next : options
}
